import { supabase } from './database/supabase';
import { ErrorType, handleApiError, useError } from '@/shared/contexts/ErrorContext';
import type { FilterOptions } from '@/types/api';
import type { Nomination } from '@/types/models/schema';

// Create a mock addError function for service layer
// In a real implementation, this would be passed from the component using the service
const mockAddError = (error: Parameters<ReturnType<typeof useError>['addError']>[0]) => {
  console.error('API Error:', error);
};

export const nominationService = {
  // Get all nominations with filtering
  getAll: async (filters?: FilterOptions): Promise<Nomination[] | null> => {
    try {
      let query = supabase.from('nominations').select('*');
      
      // Apply filters if provided
      if (filters) {
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        
        if (filters.search) {
          query = query.or(`nominee_name.ilike.%${filters.search}%,nominee_email.ilike.%${filters.search}%,nominee_company.ilike.%${filters.search}%,nominator_name.ilike.%${filters.search}%`);
        }
        
        // Pagination
        if (filters.limit) {
          query = query.limit(filters.limit);
        }
        
        if (filters.page && filters.limit) {
          const offset = (filters.page - 1) * filters.limit;
          query = query.range(offset, offset + filters.limit - 1);
        }
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data as Nomination[];
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: 'Failed to fetch nominations'
      });
      return null;
    }
  },
  
  // Get nomination by ID
  getById: async (id: string): Promise<Nomination | null> => {
    try {
      const { data, error } = await supabase
        .from('nominations')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      return data as Nomination;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: `Failed to fetch nomination with ID ${id}`
      });
      return null;
    }
  },
  
  // Create nomination
  create: async (data: Omit<Nomination, 'id' | 'created_at'>): Promise<Nomination | null> => {
    try {
      // Ensure location is properly handled for null values
      const safeData = {
        ...data,
        location: data.location || undefined,
        nominee_company: data.nominee_company || undefined,
        nominee_title: data.nominee_title || undefined,
        reason: data.reason || undefined
      };
      
      const { data: newNomination, error } = await supabase
        .from('nominations')
        .insert([{ ...safeData, created_at: new Date().toISOString() }])
        .select()
        .single();
      
      if (error) throw error;
      
      return newNomination as Nomination;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: 'Failed to create nomination'
      });
      return null;
    }
  },
  
  // Update nomination
  update: async (id: string, data: Partial<Nomination>): Promise<Nomination | null> => {
    try {
      // Ensure location is properly handled for null values
      const safeData = {
        ...data,
        location: data.location || undefined,
        nominee_company: data.nominee_company || undefined,
        nominee_title: data.nominee_title || undefined,
        reason: data.reason || undefined
      };
      
      const { data: updatedNomination, error } = await supabase
        .from('nominations')
        .update(safeData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return updatedNomination as Nomination;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: `Failed to update nomination with ID ${id}`
      });
      return null;
    }
  },
  
  // Delete nomination
  delete: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('nominations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: `Failed to delete nomination with ID ${id}`
      });
      return false;
    }
  },
  
  // Convert nomination to guest
  convertToGuest: async (id: string): Promise<boolean> => {
    try {
      // First get the nomination
      const { data: nomination, error: fetchError } = await supabase
        .from('nominations')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Create a new guest from the nomination data
      const { error: createError } = await supabase
        .from('guests')
        .insert([{
          name: nomination.nominee_name,
          email: nomination.nominee_email,
          company: nomination.nominee_company || undefined,
          title: nomination.nominee_title || undefined,
          status: 'pending',
          location: nomination.location || undefined,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);
      
      if (createError) throw createError;
      
      // Update the nomination status to converted
      const { error: updateError } = await supabase
        .from('nominations')
        .update({ 
          status: 'approved'
        })
        .eq('id', id);
      
      if (updateError) throw updateError;
      
      return true;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: `Failed to convert nomination with ID ${id} to guest`
      });
      return false;
    }
  }
};

export default nominationService;
